import { useEffect, useState } from "react";
import DashboardLayout from "../../components/DashboardLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { toast } from "sonner";
import { Sparkles, ScrollText } from "lucide-react";

export default function PapersPage() {
  const [regs, setRegs] = useState([]);
  const [events, setEvents] = useState([]);
  const [papers, setPapers] = useState([]);
  const [councils, setCouncils] = useState({});
  const [form, setForm] = useState({ event_id: "", council_id: "", country_name: "", topic: "", content: "" });
  const [submitting, setSubmitting] = useState(false);
  const [activePaper, setActivePaper] = useState(null);
  const [evaluating, setEvaluating] = useState(false);

  useEffect(() => {
    Promise.all([api.get("/registrations"), api.get("/events")]).then(async ([r, e]) => {
      setRegs(r.data);
      setEvents(e.data);
      const allCouncils = {};
      for (const reg of r.data) {
        const c = await api.get(`/councils?event_id=${reg.event_id}`);
        allCouncils[reg.event_id] = c.data;
      }
      setCouncils(allCouncils);
    });
    loadPapers();
  }, []);

  const loadPapers = () => api.get("/papers").then((r) => setPapers(r.data));

  const onRegSelect = (event_id) => {
    const reg = regs.find((r) => r.event_id === event_id);
    const council = councils[event_id]?.find((c) => c.id === reg?.allocated_council_id);
    setForm({
      event_id,
      council_id: reg?.allocated_council_id || "",
      country_name: "",
      topic: council?.topic || "",
      content: "",
    });
    if (reg?.allocated_country_id) {
      api.get(`/countries?event_id=${event_id}`).then((c) => {
        const country = c.data.find((x) => x.id === reg.allocated_country_id);
        setForm((f) => ({ ...f, country_name: country?.name || "" }));
      });
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/papers", form);
      toast.success("Paper submitted");
      loadPapers();
      setForm({ event_id: "", council_id: "", country_name: "", topic: "", content: "" });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setSubmitting(false);
  };

  const evaluate = async (paperId) => {
    setEvaluating(true);
    try {
      const { data } = await api.post(`/papers/${paperId}/evaluate`);
      toast.success(`AI evaluation done: ${data.overall_score}/100`);
      await loadPapers();
      const p = papers.find((x) => x.id === paperId);
      setActivePaper({ ...(p || {}), ai_evaluation: data, id: paperId });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    }
    setEvaluating(false);
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto grid lg:grid-cols-12 gap-6">
        <div className="lg:col-span-5">
          <p className="text-[10px] uppercase tracking-[0.28em] text-[#D4AF37] font-bold">
            Submission
          </p>
          <h1 className="mt-2 text-3xl sm:text-4xl font-bold tracking-tighter text-[#0A192F]">
            Position paper
          </h1>
          <form onSubmit={submit} className="mt-8 space-y-4" data-testid="paper-form">
            <div>
              <Label>Event</Label>
              <Select value={form.event_id} onValueChange={onRegSelect}>
                <SelectTrigger data-testid="paper-event-select"><SelectValue placeholder="Select event" /></SelectTrigger>
                <SelectContent>
                  {regs.map((r) => {
                    const ev = events.find((e) => e.id === r.event_id);
                    return <SelectItem key={r.event_id} value={r.event_id}>{ev?.title || r.event_id}</SelectItem>;
                  })}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Country / Portfolio</Label>
              <Input value={form.country_name} onChange={(e) => setForm({ ...form, country_name: e.target.value })} placeholder="USA" data-testid="paper-country-input" />
            </div>
            <div>
              <Label>Topic</Label>
              <Input value={form.topic} onChange={(e) => setForm({ ...form, topic: e.target.value })} data-testid="paper-topic-input" />
            </div>
            <div>
              <Label>Paper content</Label>
              <Textarea rows={8} value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Paste your 300–800 word position paper here…" data-testid="paper-content-input" />
            </div>
            <Button type="submit" disabled={submitting || !form.event_id || !form.council_id} className="w-full h-12 bg-[#0F4C81] hover:bg-[#0A3860]" data-testid="paper-submit-button">
              {submitting ? "Submitting…" : "Submit paper"}
            </Button>
          </form>
        </div>

        <div className="lg:col-span-7">
          <h2 className="text-lg font-semibold tracking-tight text-[#0A192F]">My papers</h2>
          <div className="mt-4 space-y-4">
            {papers.map((p) => (
              <div key={p.id} className="rounded-xl bg-white border border-slate-200 p-5">
                <div className="flex items-start justify-between flex-wrap gap-3">
                  <div>
                    <p className="text-[10px] uppercase tracking-[0.22em] text-[#0F4C81] font-bold">
                      {p.country_name || "—"} · {p.status}
                    </p>
                    <h3 className="mt-1 font-semibold text-[#0A192F]">{p.topic}</h3>
                    <p className="text-xs text-slate-500 mt-0.5">
                      Submitted {new Date(p.submitted_at).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" onClick={() => setActivePaper(p)} data-testid={`view-paper-${p.id}`}>
                      View
                    </Button>
                    <Button
                      size="sm"
                      disabled={evaluating}
                      onClick={() => evaluate(p.id)}
                      className="bg-[#D4AF37] text-[#0A192F] hover:bg-[#B5952F] font-bold"
                      data-testid={`ai-evaluate-${p.id}`}
                    >
                      <Sparkles className="h-3.5 w-3.5 mr-1" /> {evaluating ? "Evaluating…" : "AI Evaluate"}
                    </Button>
                  </div>
                </div>
                {p.ai_evaluation && (
                  <div className="mt-4 p-4 rounded-md bg-[#0A192F] text-white">
                    <div className="flex items-center justify-between flex-wrap gap-2">
                      <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">AI Score</p>
                      <p className="font-serif text-2xl text-[#D4AF37]">{p.ai_evaluation.overall_score}/100</p>
                    </div>
                    <div className="mt-3 grid grid-cols-4 gap-2 text-xs">
                      {[
                        ["Research", p.ai_evaluation.research_score],
                        ["Policy", p.ai_evaluation.policy_score],
                        ["Solutions", p.ai_evaluation.solutions_score],
                        ["Writing", p.ai_evaluation.writing_score],
                      ].map(([k, v]) => (
                        <div key={k} className="rounded bg-white/5 p-2 text-center">
                          <p className="text-[9px] uppercase tracking-[0.18em] text-slate-400">{k}</p>
                          <p className="font-mono text-[#D4AF37] text-lg">{v}</p>
                        </div>
                      ))}
                    </div>
                    <p className="mt-3 text-xs text-slate-300">{p.ai_evaluation.summary}</p>
                  </div>
                )}
              </div>
            ))}
            {papers.length === 0 && (
              <div className="rounded-xl bg-white border-2 border-dashed border-slate-200 p-10 text-center">
                <ScrollText className="h-5 w-5 text-slate-400 mx-auto" />
                <p className="mt-2 text-sm text-slate-500">No papers yet.</p>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Paper dialog */}
      {activePaper && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-6" onClick={() => setActivePaper(null)}>
          <div className="max-w-2xl w-full bg-white rounded-xl p-8 overflow-auto max-h-[90vh]" onClick={(e) => e.stopPropagation()} data-testid="paper-view-dialog">
            <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">
              {activePaper.country_name} · {activePaper.topic}
            </p>
            <h2 className="mt-2 text-2xl font-bold tracking-tighter text-[#0A192F]">Position Paper</h2>
            <p className="mt-6 text-sm text-slate-700 whitespace-pre-wrap leading-relaxed">
              {activePaper.content}
            </p>
            {activePaper.ai_evaluation && (
              <>
                <hr className="my-6 border-slate-200" />
                <p className="text-[10px] uppercase tracking-[0.22em] text-[#D4AF37] font-bold">AI Evaluation</p>
                <p className="mt-2 text-sm">{activePaper.ai_evaluation.summary}</p>
                {activePaper.ai_evaluation.strengths?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-[#0A192F]">Strengths</p>
                    <ul className="list-disc ml-5 text-sm text-slate-600">
                      {activePaper.ai_evaluation.strengths.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
                {activePaper.ai_evaluation.recommendations?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs font-semibold text-[#0A192F]">Recommendations</p>
                    <ul className="list-disc ml-5 text-sm text-slate-600">
                      {activePaper.ai_evaluation.recommendations.map((s, i) => <li key={i}>{s}</li>)}
                    </ul>
                  </div>
                )}
              </>
            )}
            <div className="mt-6 text-right">
              <Button onClick={() => setActivePaper(null)} data-testid="close-paper-dialog">Close</Button>
            </div>
          </div>
        </div>
      )}
    </DashboardLayout>
  );
}
